import React, { useState, useEffect } from 'react';
import { GlobalMicroButton } from '../../GlobalMicroButton';
import { StatusBadge } from '../../StatusBadge';
import styles from './RiskCard.module.css';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { useTheme } from '../../../context/ThemeProvider';
import { RiskRecord } from '../../../types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

interface RiskCardProps {
  risk: RiskRecord;
  projectId: string;
  onEdit?: (risk: RiskRecord) => void;
  onDelete?: (riskId: string) => void;
  onViewMitigation?: (risk: RiskRecord) => void;
  onEntityClick?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'SOP') => void;
  compact?: boolean;
}

export const RiskCard: React.FC<RiskCardProps> = ({
  risk,
  projectId,
  onEdit,
  onDelete,
  onViewMitigation,
  onEntityClick,
  compact = false
}) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);
  const [entityTitles, setEntityTitles] = useState<Record<string, string>>({});

  // Fetch entity titles for connected entities
  useEffect(() => {
    const fetchEntityTitles = async () => {
      if (!projectId || !isExpanded) return;

      const titles: Record<string, string> = {};

      // Fetch URS titles
      if (risk.links.urs_ids.length > 0) {
        try {
          const ursCol = collection(db, 'projects', projectId, 'urs');
          const ursSnapshot = await getDocs(ursCol);
          ursSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            if (risk.links.urs_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching URS titles:', err);
          }
        }
      }

      // Fetch FS titles
      if (risk.links.fs_ids.length > 0) {
        try {
          const fsCol = collection(db, 'projects', projectId, 'fs');
          const fsSnapshot = await getDocs(fsCol);
          fsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            if (risk.links.fs_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching FS titles:', err);
          }
        }
      }

      // Fetch TEST titles
      if (risk.links.test_ids.length > 0) {
        try {
          const testCol = collection(db, 'projects', projectId, 'tests');
          const testSnapshot = await getDocs(testCol);
          testSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
            if (risk.links.test_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching TEST titles:', err);
          }
        }
      }

      // Fetch SOP titles
      if (risk.links.sop_ids.length > 0) {
        try {
          const sopCol = collection(db, 'projects', projectId, 'sops');
          const sopSnapshot = await getDocs(sopCol);
          sopSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
            if (risk.links.sop_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching SOP titles:', err);
          }
        }
      }

      setEntityTitles(titles);
    };

    fetchEntityTitles();
  }, [projectId, risk.links, isExpanded]);

  const getRPNColor = (rating: string) => {
    if (rating === 'High') return styles.rpnHigh;
    if (rating === 'Medium') return styles.rpnMedium;
    return styles.rpnLow;
  };

  const getMitigationStatusBadge = (status: string) => {
    const statusMap: Record<string, { textKey: string; variant: 'success' | 'warning' | 'info' | 'error' }> = {
      Approved: { textKey: 'global_status_approved', variant: 'success' },
      Draft: { textKey: 'global_status_draft', variant: 'info' },
      'In Review': { textKey: 'global_status_in_review', variant: 'warning' },
      Retired: { textKey: 'global_status_rejected', variant: 'error' }
    };
    const statusInfo = statusMap[status] || { textKey: status.toString(), variant: 'info' as const };
    return <StatusBadge text={t(statusInfo.textKey)} variant={statusInfo.variant} size="medium" />;
  };

  const formatDate = (date: any) => {
    try {
      return new Date(date?.seconds ? date.seconds * 1000 : date).toLocaleDateString();
    } catch {
      return '';
    }
  };

  if (compact) {
    return (
      <div className={styles.riskCardCompact}>
        <div className={styles.compactHeader}>
          <span className={styles.riskId}>{risk.id}</span>
          <span className={`${styles.rpnCompact} ${getRPNColor(risk.initial_risk_rating)}`}>
            Rating: {risk.initial_risk_rating}
          </span>
          {getMitigationStatusBadge(risk.status)}
        </div>
        <p className={styles.compactDescription}>{risk.title}</p>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.cardActions}`)) {
      return;
    }
    if (target.closest('button') && !target.closest(`.${styles.cardHeader}`)) {
      return;
    }
    if (target.closest(`.${styles.entityLinkItem}`)) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      id={`entity-${risk.id}`}
      data-entity-card
      className={styles.riskCard}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <h4 className={styles.riskId}>
            {risk.id}: {risk.title}
          </h4>
        </div>
        <div className={styles.headerRight}>
          {getMitigationStatusBadge(risk.status)}
          <div
            data-expand-trigger
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
          >
            <img
              src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"}
              alt={isExpanded ? "Collapse" : "Expand"}
              width={12}
              height={12}
            />
          </div>
        </div>
      </div>

      <p className={styles.description}>{risk.risk_statement}</p>

      <div className={`${styles.riskDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
        <div className={styles.riskDetailsInner}>

          {/* Inline meta fields */}
          <div className={styles.inlineMeta}>

            {/* Cause */}
            {risk.cause && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_cause') || 'Cause'}:</span>
                <span className={styles.metaValue}> {risk.cause}</span>
              </div>
            )}

            {/* Consequence */}
            {risk.consequence && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_consequence') || 'Consequence'}:</span>
                <span className={styles.metaValue}> {risk.consequence}</span>
              </div>
            )}

            {/* Severity */}
            <div>
              <span className={styles.metaLabel}>{t('project_tab_severity') || 'Severity'}:</span>
              <span className={styles.metaValue}> {risk.severity}/5</span>
            </div>

            {/* Likelihood */}
            <div>
              <span className={styles.metaLabel}>{t('project_tab_likelihood') || 'Likelihood'}:</span>
              <span className={styles.metaValue}> {risk.likelihood}/5</span>
            </div>

            {/* Detectability */}
            <div>
              <span className={styles.metaLabel}>{t('project_tab_detectability') || 'Detectability'}:</span>
              <span className={styles.metaValue}> {risk.detectability ?? 0}/5</span>
            </div>

            {/* Initial Rating */}
            <div>
              <span className={styles.metaLabel}>{t('project_tab_initial_rating') || 'Initial'}:</span>
              <span className={`${styles.metaValue} ${getRPNColor(risk.initial_risk_rating)}`}>
                {' '}{risk.initial_risk_rating}
              </span>
            </div>

            {/* Residual Rating */}
            <div>
              <span className={styles.metaLabel}>{t('project_tab_residual_rating') || 'Residual'}:</span>
              <span className={`${styles.metaValue} ${getRPNColor(risk.residual_risk_rating)}`}>
                {' '}{risk.residual_risk_rating}
              </span>
            </div>

            {/* Owner 
            {risk.meta?.owner && (
              <div>
                <span className={styles.metaValue}>{risk.meta.owner}</span>
              </div>
            )}*/}
          </div>

          {/* Technical and Procedural Controls */}
          {((risk.controls?.technical && risk.controls.technical.length > 0) || (risk.controls?.procedural && risk.controls.procedural.length > 0)) && (
            <div className={styles.inlineMeta}>
              {risk.controls?.technical && risk.controls.technical.length > 0 && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_technical_controls')}:</span>
                  <span className={styles.metaValue}> {risk.controls.technical.join(', ')}</span>
                </div>
              )}
              {risk.controls?.procedural && risk.controls.procedural.length > 0 && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_procedural_controls')}:</span>
                  <span className={styles.metaValue}> {risk.controls.procedural.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {/* Dates on separate line */}
          {(risk.createdAt || risk.updatedAt) && (
            <div className={styles.inlineMeta}>
              {risk.createdAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_created')}:</span>
                  <span className={styles.metaValue}> {formatDate(risk.createdAt)}</span>
                </div>
              )}
              {risk.updatedAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_updated')}:</span>
                  <span className={styles.metaValue}> {formatDate(risk.updatedAt)}</span>
                </div>
              )}
            </div>
          )}

          {/* Connected Entities */}
          {(risk.links.urs_ids.length > 0 || risk.links.fs_ids.length > 0 || risk.links.test_ids.length > 0 || risk.links.sop_ids.length > 0) && (
            <div className={styles.connectedEntitiesSection}>
              {risk.links.urs_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_urs') || 'Linked URS'}:</span>
                  <div className={styles.entityList}>
                    {risk.links.urs_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'URS');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {risk.links.fs_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_fs') || 'Linked FS'}:</span>
                  <div className={styles.entityList}>
                    {risk.links.fs_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'FS');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {risk.links.test_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_tests') || 'Linked TEST'}:</span>
                  <div className={styles.entityList}>
                    {risk.links.test_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'TEST');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {risk.links.sop_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_sops') || 'Linked SOP'}:</span>
                  <div className={styles.entityList}>
                    {risk.links.sop_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'SOP');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Card actions */}
          <div
            className={styles.cardActions}
            onClick={(e) => e.stopPropagation()}
          >

            {/* View mitigation button 
            {onViewMitigation && (
              <GlobalMicroButton 
                onClick={() => onViewMitigation(risk)}
                icon={<img src={isDarkMode ? "/assets/icons/eye.svg" : "/assets/icons/eye.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_view_mitigation')}
              </GlobalMicroButton>
            )}*/}

            {/* Edit button */}
            {onEdit && (
              <GlobalMicroButton
                onClick={() => onEdit(risk)}
                icon={<img src={isDarkMode ? "/assets/icons/pencil.svg" : "/assets/icons/pencil.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_edit')}
              </GlobalMicroButton>
            )}

            {/* Delete button */}
            {onDelete && (
              <GlobalMicroButton
                onClick={() => onDelete(risk.id)}
                icon={<img src={isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_delete')}
              </GlobalMicroButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

